import { useRef } from "react";
import { modelTabId, modelTabPanelId, type TabKey } from "./bedrockModelsDraft";
import styles from "./BedrockModels.module.css";
import { Dropdown } from "../shared/Dropdown";

/** One tab: its key, the name it shows and how many models it would list under the current search. */
export interface ModelTabDefinition {
  key: TabKey;
  label: string;
  count: number;
}

/**
 * The tab strip of the Bedrock Model Management page: All, Enabled, then one tab per provider, each with its count. Roving tab index
 * with Left, Right, Home and End moving between tabs (the ARIA tabs pattern). On a phone (`narrow`) the strip is a drop-down instead.
 * The panel itself is rendered by the page, because it is one scrolling list whose content depends on the active tab.
 */
export function ModelTabs({
  tabs,
  activeKey,
  onSelect,
  narrow,
  dropdownLabel,
}: {
  tabs: ModelTabDefinition[];
  activeKey: TabKey;
  onSelect: (key: TabKey) => void;
  narrow: boolean;
  dropdownLabel: string;
}): React.JSX.Element {
  const buttons = useRef<Record<string, HTMLButtonElement | null>>({});

  if (narrow) {
    return (
      <div className={styles.field}>
        <label htmlFor="bedrock-tab-dropdown">{dropdownLabel}</label>
        <Dropdown
          id="bedrock-tab-dropdown"
          options={tabs.map((tab) => ({ value: tab.key, label: `${tab.label} (${String(tab.count)})` }))}
          value={activeKey}
          onChange={onSelect}
        />
      </div>
    );
  }

  const move = (index: number): void => {
    const target = tabs[(index + tabs.length) % tabs.length];
    onSelect(target.key);
    buttons.current[target.key]?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number): void => {
    switch (event.key) {
      case "ArrowRight":
        event.preventDefault();
        move(index + 1);
        break;
      case "ArrowLeft":
        event.preventDefault();
        move(index - 1);
        break;
      case "Home":
        event.preventDefault();
        move(0);
        break;
      case "End":
        event.preventDefault();
        move(tabs.length - 1);
        break;
      default:
        break;
    }
  };

  return (
    <div role="tablist" className={styles.tabList}>
      {tabs.map((tab, index) => (
        <button
          key={tab.key}
          ref={(element) => {
            buttons.current[tab.key] = element;
          }}
          type="button"
          role="tab"
          id={modelTabId(tab.key)}
          aria-selected={tab.key === activeKey}
          aria-controls={modelTabPanelId}
          tabIndex={tab.key === activeKey ? 0 : -1}
          className={tab.key === activeKey ? styles.tabActive : styles.tab}
          onClick={() => onSelect(tab.key)}
          onKeyDown={(event) => onKeyDown(event, index)}
        >
          {`${tab.label} (${String(tab.count)})`}
        </button>
      ))}
    </div>
  );
}
